/**
 * Phase 127 — Operator-side booking request inbox.
 *
 * Embedded on /arenax/host/venue/manage.  Lists all bookings filtered by
 * state (default: pending — `requested` + `accepted`) and lets the
 * operator Accept / Decline / View.  Accept fires a Razorpay order
 * creation server-side and shows the resulting order_id to the operator.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { trackContextMenu } from '@/lib/analytics';
import ArenaxPaymentModeBanner from './ArenaxPaymentModeBanner';
import { BulletproofContextMenu } from '@/components/ui/bulletproof-context-menu';
import RescheduleBookingDialog from '@/components/arenax/dialogs/RescheduleBookingDialog';
import ExtendSessionDialog from '@/components/arenax/dialogs/ExtendSessionDialog';
import {
  Inbox, Loader2, CheckCircle2, X, Clock, IndianRupee, User,
  Calendar, MessageSquare, RefreshCcw, ArrowRight,
  Eye, RotateCcw, AlertTriangle, CalendarClock, PauseCircle, UserCheck, Award,
  Copy,
} from 'lucide-react';

const STATE_FILTERS = [
  { id: 'pending', label: 'Pending', filter: 'requested', count_states: ['requested'] },
  { id: 'accepted', label: 'Awaiting payment', filter: 'accepted' },
  { id: 'confirmed', label: 'Confirmed', filter: 'confirmed' },
  { id: 'all', label: 'All', filter: null },
];

function fmtDateTime(s) {
  return new Date(s).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Phase 178.5.G — operator action surface for a single booking row.
 *
 * Intent-color semantics consistently applied:
 *   • neutral — informational (View, copy ID, view player, chat)
 *   • success — forward-momentum actions (Accept, Mark arrived, Complete)
 *   • warning — pause-the-world actions (Reschedule, Hold)
 *   • danger  — value-destructive actions (Decline/Cancel, Refund, No-show)
 *
 * Items are state-aware: a `confirmed` booking can't be Accepted again, a
 * `requested` booking can't be Refunded, etc.  Items without a backend
 * endpoint yet surface as `disabled: true` to preserve the visual
 * hierarchy and tease the future capability surface to operators.
 */
function buildBookingActionItems(b, handlers) {
  const isRequested = b.state === 'requested';
  const isAccepted  = b.state === 'accepted';
  const isConfirmed = b.state === 'confirmed';
  const items = [
    // ── NEUTRAL — informational ──────────────────────────────────
    { id: 'copy-id', label: 'Copy booking ID', icon: Copy,
      action: handlers.copyId, variant: 'neutral' },
    { id: 'view-player', label: 'View player', icon: User,
      disabled: true, variant: 'neutral' },
    { id: 'message-player', label: 'Message player', icon: MessageSquare,
      disabled: true, variant: 'neutral' },
    { separator: true },

    // ── SUCCESS — forward momentum ───────────────────────────────
    isRequested && { id: 'accept', label: 'Accept booking', icon: CheckCircle2,
      action: handlers.accept, variant: 'success' },
    isConfirmed && { id: 'mark-arrived', label: 'Mark arrived', icon: UserCheck,
      disabled: true, variant: 'success' },
    isConfirmed && { id: 'complete', label: 'Complete session', icon: Award,
      disabled: true, variant: 'success' },

    // ── WARNING — pause-the-world ────────────────────────────────
    (isRequested || isAccepted) && { id: 'reschedule', label: 'Reschedule',
      icon: CalendarClock, action: handlers.reschedule, variant: 'warning' },
    (isRequested || isAccepted) && { id: 'hold', label: 'Hold temporarily',
      icon: PauseCircle, disabled: true, variant: 'warning' },
    (isAccepted || isConfirmed) && { id: 'extend', label: 'Extend session',
      icon: CalendarClock, action: handlers.extend, variant: 'warning' },
    { separator: true },

    // ── DANGER — value-destructive ───────────────────────────────
    (isRequested || isAccepted) && { id: 'decline', label: isRequested ? 'Decline request' : 'Cancel booking',
      icon: X, action: handlers.decline, variant: 'danger' },
    (isConfirmed || isAccepted) && { id: 'refund', label: 'Refund player', icon: RotateCcw,
      action: handlers.refund, variant: 'danger' },
    isConfirmed && { id: 'no-show', label: 'Mark no-show', icon: AlertTriangle,
      action: handlers.noShow, variant: 'danger' },
  ].filter(Boolean);
  return items;
}

export default function ArenaxBookingInbox({ venueId, onAcceptedRefresh }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [acting, setActing] = useState(null);
  const [declineReason, setDeclineReason] = useState({});
  // Phase 178.6.F — dialog state for the premium reschedule + extend flows
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [extendBooking, setExtendBooking] = useState(null);

  useEffect(() => {
    if (!venueId) return;
    load();
    // eslint-disable-next-line
  }, [venueId, activeFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const cfg = STATE_FILTERS.find(s => s.id === activeFilter);
      const url = cfg.filter
        ? `/arenax/host/venue/${venueId}/slot-bookings?state=${cfg.filter}`
        : `/arenax/host/venue/${venueId}/slot-bookings`;
      const r = await api.get(url);
      setBookings(r.data?.bookings || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load bookings');
    } finally { setLoading(false); }
  };

  const accept = async (b) => {
    setActing(b.id);
    try {
      const r = await api.post(`/arenax/host/venue/${venueId}/slot-bookings/${b.id}/accept`);
      toast.success(
        r.data?.razorpay?.mock_mode
          ? 'Accepted · mock payment link generated'
          : `Accepted · Razorpay order ${r.data?.razorpay?.order_id?.slice(0, 16)}…`
      );
      await load();
      onAcceptedRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not accept');
    } finally { setActing(null); }
  };

  const decline = async (b) => {
    setActing(b.id);
    try {
      await api.post(`/arenax/host/venue/${venueId}/slot-bookings/${b.id}/decline`, {
        reason: declineReason[b.id] || '',
      });
      toast.success('Declined · slot returned to pool');
      setDeclineReason(r => ({ ...r, [b.id]: '' }));
      await load();
      onAcceptedRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not decline');
    } finally { setActing(null); }
  };

  return (
    <div className="arenax-card p-6 rounded-2xl" data-testid="arenax-booking-inbox">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-bold tracking-tight flex items-center gap-1.5"
              style={{ color: 'hsl(var(--ax-text))' }}>
            <Inbox className="h-4 w-4" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
            Booking inbox
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            Player requests waiting for your call.
          </p>
        </div>
        <button onClick={load}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}
                data-testid="booking-inbox-refresh">
          <RefreshCcw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Phase 128 P1 — Payment mode banner */}
      <div className="mb-4">
        <ArenaxPaymentModeBanner variant="strip" hideWhenLive testId="booking-inbox-payment-mode" />
      </div>

      {/* State filter chips */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1" data-testid="booking-inbox-filters">
        {STATE_FILTERS.map(f => {
          const active = activeFilter === f.id;
          return (
            <button key={f.id}
                    onClick={() => setActiveFilter(f.id)}
                    data-testid={`booking-inbox-filter-${f.id}`}
                    className="text-[11px] font-bold px-3 py-1.5 rounded-full whitespace-nowrap transition-all"
                    style={{
                      background: active ? 'var(--ax-primary-gradient)' : 'var(--ax-card-bg-soft)',
                      color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-text-muted))',
                      border: active ? 'none' : '1px solid var(--ax-border-quiet)',
                    }}>
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-10">
          <Inbox className="h-7 w-7 mx-auto mb-2" style={{ color: 'hsl(var(--ax-text-faint))' }} />
          <p className="text-xs italic" style={{ color: 'hsl(var(--ax-text-faint))' }}>
            No bookings in this view.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map(b => {
            const lockMins = b.lock_expires_at
              ? Math.max(0, Math.round((new Date(b.lock_expires_at) - new Date()) / 60000))
              : 0;
            const isRequested = b.state === 'requested';
            const handlers = {
              copyId: () => {
                try {
                  navigator.clipboard?.writeText(b.id);
                  toast.success('Booking ID copied');
                } catch (_) { toast.info(b.id); }
              },
              accept: () => accept(b),
              decline: () => decline(b),
              refund: async () => {
                if (!window.confirm(`Refund ₹${b.price || 0} to ${b.player_name || 'player'}?\n\nThis cannot be undone.`)) return;
                try {
                  await api.post(`/arenax/bookings/${b.id}/refund`, { reason: null });
                  toast.success('Refunded');
                  await load();
                  onAcceptedRefresh?.();
                } catch (err) { toast.error(err?.response?.data?.detail || 'Refund failed'); }
              },
              noShow: async () => {
                if (!window.confirm(`Mark ${b.player_name || 'player'} as no-show?\n\nThis affects their reliability score.`)) return;
                try {
                  await api.post(`/arenax/bookings/${b.id}/no-show`, { note: null });
                  toast.success('Marked no-show');
                  await load();
                  onAcceptedRefresh?.();
                } catch (err) { toast.error(err?.response?.data?.detail || 'Action failed'); }
              },
              reschedule: () => setRescheduleBooking(b),
              extend: () => setExtendBooking(b),
            };
            const actionItems = buildBookingActionItems(b, handlers);
            return (
              <BulletproofContextMenu
                key={b.id}
                items={actionItems}
                metadata={{
                  entity_type: 'venue_booking',
                  row_id: b.id,
                  booking_state: b.state,
                  venue_id: venueId,
                }}
                onMenuOpen={(m) => trackContextMenu('opened', m)}
                onActionSelected={(item, m) => trackContextMenu('selected', {
                  ...m, action_id: item.id, action_label: item.label, variant: item.variant,
                })}
                onCancelled={(m) => trackContextMenu('cancelled', m)}
              >
                <div className="rounded-xl p-4 border"
                     style={{ borderColor: isRequested ? 'rgba(245,158,11,0.30)' : 'var(--ax-border-quiet)',
                              background: isRequested ? 'rgba(245,158,11,0.04)' : 'var(--ax-card-bg-soft)',
                              cursor: 'context-menu' }}
                     data-testid={`booking-inbox-row-${b.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1"
                           style={{ color: isRequested ? '#92400e' : 'hsl(var(--ax-text-faint))' }}>
                        {b.state}
                        {isRequested && lockMins > 0 && ` · ${lockMins}m left`}
                      </div>
                      <div className="text-base font-bold mb-2" style={{ color: 'hsl(var(--ax-text))' }}>
                        {fmtDateTime(b.start_at)}
                      </div>
                      <div className="text-xs flex flex-wrap items-center gap-x-4 gap-y-1.5"
                           style={{ color: 'hsl(var(--ax-text-muted))' }}>
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3 w-3" /> {b.player_name || 'Player'}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(b.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {' – '}
                          {new Date(b.end_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                        {b.price ? (
                          <span className="inline-flex items-center gap-0.5 font-bold"
                                style={{ color: 'hsl(var(--ax-text))' }}>
                            <IndianRupee className="h-3 w-3" />{b.price}
                          </span>
                        ) : null}
                        {b.party_size ? (
                          <span>· party of {b.party_size}</span>
                        ) : null}
                        <span className="inline-flex items-center gap-1 text-[10px] opacity-70"
                              title="Right-click anywhere on this row for the full action menu">
                          ⌥ right-click for actions
                        </span>
                      </div>
                      {b.notes && (
                        <div className="text-[11px] mt-2 italic flex items-start gap-1"
                             style={{ color: 'hsl(var(--ax-text-muted))' }}>
                          <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          "{b.notes}"
                        </div>
                      )}
                      {b.state === 'accepted' && b.mock_payment && (
                        <div className="text-[11px] mt-2 italic"
                             style={{ color: '#92400e' }}>
                          Mock payment link · player can self-confirm without Razorpay
                        </div>
                      )}
                    </div>
                    {isRequested && (
                      <div className="flex flex-col items-end gap-1.5" style={{ minWidth: 200 }}>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => accept(b)}
                                  disabled={acting === b.id}
                                  className="text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                                  data-testid={`booking-inbox-accept-${b.id}`}>
                            {acting === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                            Accept <ArrowRight className="h-3 w-3" />
                          </button>
                          <button onClick={() => decline(b)}
                                  disabled={acting === b.id}
                                  className="text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-60"
                                  style={{ color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}
                                  data-testid={`booking-inbox-decline-${b.id}`}>
                            <X className="h-3 w-3" /> Decline
                          </button>
                        </div>
                        <input
                          value={declineReason[b.id] || ''}
                          onChange={e => setDeclineReason(r => ({ ...r, [b.id]: e.target.value }))}
                          placeholder="Decline reason (optional)"
                          className="arenax-input w-full px-2.5 py-1 rounded text-[11px]"
                          data-testid={`booking-inbox-decline-reason-${b.id}`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </BulletproofContextMenu>
            );
          })}
        </div>
      )}

      {/* Phase 178.6.F — premium dialogs replace window.prompt/confirm */}
      <RescheduleBookingDialog
        booking={rescheduleBooking}
        open={!!rescheduleBooking}
        onClose={() => setRescheduleBooking(null)}
        onSuccess={() => { load(); onAcceptedRefresh?.(); }}
      />
      <ExtendSessionDialog
        booking={extendBooking}
        open={!!extendBooking}
        onClose={() => setExtendBooking(null)}
        onSuccess={() => { load(); onAcceptedRefresh?.(); }}
      />
    </div>
  );
}
